import { useEffect, useMemo, useState } from "react";
import PageLayout from "@/components/layout/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { chatbotService, ChatSessionSummaryDto, ChatSessionDetailDto } from "@/services/chatbotService";
import { formatDistanceToNow } from "date-fns";
import { Loader2, Search, Filter, Mic } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { projectService, ProjectDto } from "@/services/projectService";
import { calculateCredits, type CreditUsage } from "@/lib/credits";

type ChatMessage = { role: string; content: string; timestamp?: string; order?: number; source?: string; isVoice?: boolean };

type SessionUsage = CreditUsage;

type ChatSessionSummary = {
  id: string;
  userId: string;
  createdAt: string;
  usage: SessionUsage;
  credit: number;
};

const parseNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const ensureIsoString = (value: unknown): string => {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "string" && value) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.toISOString();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.toISOString();
  }
  return new Date().toISOString();
};

const extractUsage = (input?: Partial<ChatSessionSummaryDto>): SessionUsage => {
  const record = input as Record<string, unknown> | undefined;
  return {
    promptTokens: parseNumber(input?.promptTokens ?? record?.["prompt_tokens"]),
    completionTokens: parseNumber(input?.completionTokens ?? record?.["completion_tokens"]),
    textIn: parseNumber(input?.textIn ?? record?.["text_in"]),
    textOut: parseNumber(input?.textOut ?? record?.["text_out"]),
    audioIn: parseNumber(input?.audioIn ?? record?.["audio_in"]),
    audioOut: parseNumber(input?.audioOut ?? record?.["audio_out"]),
  };
};

export default function ChatbotHistory() {
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [activeMessages, setActiveMessages] = useState<ChatMessage[] | null>(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [search, setSearch] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [availableProjects, setAvailableProjects] = useState<ProjectDto[]>([]);

  const creditFormatter = useMemo(
    () => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    []
  );
  const formatCredits = (value: number) => `${creditFormatter.format(value)} credits`;

  useEffect(() => {
    (async () => {
      try {
        const projects = await projectService.list();
        setAvailableProjects(projects);
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingSessions(true);
      try {
        const data = await chatbotService.listSessions({ userId: userFilter || undefined, projectId: projectFilter === 'all' ? undefined : projectFilter, limit: 100 });
        if (!mounted) return;
        const normalized = Array.isArray(data)
          ? data.map((d: ChatSessionSummaryDto) => {
              const usage = extractUsage(d);
              return {
                id: String(d.id ?? ""),
                userId: d.userId ?? "",
                createdAt: ensureIsoString(d.createdAt),
                usage,
                credit: calculateCredits(usage),
              } as ChatSessionSummary;
            })
          : [];
        setSessions(normalized);
      } finally {
        if (mounted) setLoadingSessions(false);
      }
    })();
    return () => { mounted = false; };
  }, [userFilter, projectFilter]);

  // Ensure a session is selected when sessions load
  useEffect(() => {
    if (!activeSessionId && sessions.length > 0) {
      setActiveSessionId(sessions[0].id);
    }
  }, [sessions, activeSessionId]);

  useEffect(() => {
    if (!activeSessionId) return;
    let mounted = true;
    (async () => {
      setLoadingMessages(true);
      try {
        const data = await chatbotService.getSession(activeSessionId);
        if (!mounted) return;
        const sessionData = (data as ChatSessionDetailDto) ?? ({} as ChatSessionDetailDto);
        setActiveMessages(sessionData.messages ?? []);
        const usage = extractUsage(sessionData);
        setSessions((prev) =>
          prev.map((session) =>
            session.id === activeSessionId
              ? { ...session, usage, credit: calculateCredits(usage) }
              : session
          )
        );
      } finally {
        if (mounted) setLoadingMessages(false);
      }
    })();
    return () => { mounted = false; };
  }, [activeSessionId]);

  const filteredSessions = useMemo(() => {
    if (!search) return sessions;
    const s = search.toLowerCase();
    return sessions.filter((it) => it.id.toLowerCase().includes(s) || it.userId.toLowerCase().includes(s));
  }, [sessions, search]);

  const activeSession = useMemo(
    () => sessions.find((session) => session.id === activeSessionId) ?? null,
    [sessions, activeSessionId]
  );

  return (
    <PageLayout
      title="Chatbot History"
      description="Review chatbot sessions and inspect full conversations"
    >
      <div className="flex gap-6 h-[calc(100vh-12rem)] min-h-[600px]">
        {/* Left: session list */}
        <Card className="w-96 flex flex-col">
          <CardHeader className="space-y-3">
            <CardTitle className="flex items-center justify-between">Sessions
              <Button size="icon" variant="ghost" className="ml-2" title="Filters">
                <Filter className="h-4 w-4" />
              </Button>
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by user or id" className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Input placeholder="Filter by userId (optional)" value={userFilter} onChange={(e) => setUserFilter(e.target.value)} />
            <div className="flex gap-2">
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {availableProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1">
            <ScrollArea className="h-full">
              {loadingSessions ? (
                <div className="flex items-center justify-center h-40 text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading sessions…</div>
              ) : filteredSessions.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground">No sessions found.</div>
              ) : (
                <div className="divide-y">
                  {filteredSessions.map((s) => (
                    <button key={s.id} className={`w-full text-left px-4 py-3 hover:bg-accent ${activeSessionId === s.id ? 'bg-accent' : ''}`} onClick={() => setActiveSessionId(s.id)}>
                      <div className="flex items-center justify-between">
                        <div className="font-medium truncate">{s.userId || 'Anonymous user'}</div>
                        <div className="text-xs text-muted-foreground ml-2 whitespace-nowrap">{formatDistanceToNow(new Date(s.createdAt), { addSuffix: true })}</div>
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{s.id}</span>
                        <span className="ml-2 whitespace-nowrap font-medium text-foreground">{formatCredits(s.credit)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Right: conversation view */}
        <Card className="flex-1 flex flex-col">
          <CardHeader className="flex flex-col gap-1">
            <CardTitle>Conversation</CardTitle>
            {activeSession && (
              <div className="text-sm text-muted-foreground">
                Credit cost: <span className="font-medium text-foreground">{formatCredits(activeSession.credit)}</span>
              </div>
            )}
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden">
            <ScrollArea className="h-full pr-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-40 text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading conversation…</div>
              ) : !activeMessages || activeMessages.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground">Select a session to view messages.</div>
              ) : (
                <div className="space-y-4">
                  {activeMessages.map((m, idx) => (
                    <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                        {m.isVoice && (
                          <div className="mb-1 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide opacity-80">
                            <Mic className="h-3 w-3" />
                            <span>Voice message</span>
                          </div>
                        )}
                        {m.content}
                        {m.source && m.role !== 'user' && (
                          <div className="mt-1 text-[10px] opacity-70">source: {m.source}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}


